import {
  RequirementWorkflowType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { Banner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { Accordion } from "@src/appV2/redesign/components/Accordion";
import { useMemo } from "react";

import { filterOutBackgroundCheckRequirements } from "../../BackgroundChecks/filterOutBackgroundCheckRequirements";
import { DocumentCardsWrapper } from "../components/DocumentCardsWrapper";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";
import { UploadMiscDocumentButton } from "./UploadMiscDocumentButton";
import { useDocumentsPageAccordionContext } from "./useAccordionProvider";
import { useDocumentsPageApiContext } from "./useApiProvider";
import { DocumentsPageWorkplaceSpecificDocumentsSection } from "./WorkplaceSpecificDocumentsSection";

export function DocumentsPageWorkplaceSpecificDocumentsContent() {
  const {
    completedWorkplaceSpecificRequirementsQuery: {
      data: completedData,
      isSuccess: isSuccessCompleted,
      hasNextPage: hasNextPageCompleted,
      isFetchingNextPage: isLoadingMoreCompleted,
      fetchNextPage: fetchNextPageCompleted,
    },
    incompleteWorkplaceSpecificRequirementsQuery: {
      data: incompleteData,
      isSuccess: isSuccessIncomplete,
      hasNextPage: hasNextPageIncomplete,
      isFetchingNextPage: isLoadingMoreIncomplete,
      fetchNextPage: fetchNextPageIncomplete,
    },
    optionalRequirementsQuery: {
      data: optionalData,
      isSuccess: isSuccessOptional,
      hasNextPage: hasNextPageOptional,
      isFetchingNextPage: isLoadingMoreOptional,
      fetchNextPage: fetchNextPageOptional,
    },
  } = useDocumentsPageApiContext();

  const {
    completedDocumentsExpanded,
    optionalDocumentsExpanded,
    setCompletedDocumentsExpanded,
    setOptionalDocumentsExpanded,
  } = useDocumentsPageAccordionContext();

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  const { completedDocuments, incompleteDocuments, optionalDocuments } = useMemo(() => {
    const completedDocuments = completedData?.pages?.flatMap((page) => page) ?? [];
    let incompleteDocuments = incompleteData?.pages?.flatMap((page) => page);
    const optionalDocuments = optionalData?.pages?.flatMap((page) => page);

    if (isRunBackgroundCheckOnBookingEnabled && isDefined(incompleteDocuments)) {
      // ACT-3188: When run-on-booking is enabled, we hide "expiring" UI for Checkr background checks.
      // Find the EXPIRING Checkr item in the incomplete list so we can treat it as completed.
      const expiringCheckrBackgroundCheck = incompleteDocuments?.find(
        (document) =>
          document.requirement?.attributes.requirementWorkflowType ===
            RequirementWorkflowType.CHECKR_BACKGROUND_CHECK &&
          document.status === WorkerRequirementStatus.EXPIRING
      );

      // ACT-3188: When run-on-booking is enabled, we map EXPIRING to APPROVED for Checkr background checks.
      // ACT-3205: When run-background-check-on-booking is enabled, we hide "missing" UI for Checkr background checks.
      incompleteDocuments = filterOutBackgroundCheckRequirements(incompleteDocuments, {
        status: [WorkerRequirementStatus.EXPIRING, WorkerRequirementStatus.MISSING],
      });

      if (isDefined(expiringCheckrBackgroundCheck)) {
        completedDocuments.push(expiringCheckrBackgroundCheck);
      }
    }

    return {
      completedDocuments,
      incompleteDocuments,
      optionalDocuments,
    };
  }, [isRunBackgroundCheckOnBookingEnabled, completedData, incompleteData, optionalData]);

  const hasApprovedDocuments =
    isSuccessCompleted && completedDocuments && completedDocuments.length > 0;
  const hasUnapprovedDocuments =
    isSuccessIncomplete && incompleteDocuments && incompleteDocuments.length > 0;
  const hasOptionalDocuments =
    isSuccessOptional && optionalDocuments && optionalDocuments.length > 0;

  // We can completely remove this case once surface shifts is enabled
  const { areAllLicensesPending } = useGetWorkerLicensesAllowedForDocuments();

  return (
    <Stack spacing={6} sx={{ overflow: "visible" }}>
      {hasUnapprovedDocuments ? (
        <DocumentCardsWrapper>
          <DocumentsPageWorkplaceSpecificDocumentsSection
            documents={incompleteDocuments}
            hasNextPage={hasNextPageIncomplete ?? false}
            isLoadingMore={isLoadingMoreIncomplete}
            onLoadMore={async () => {
              await fetchNextPageIncomplete();
            }}
          />
        </DocumentCardsWrapper>
      ) : (
        <Banner
          intent="disabled"
          illustrationType="facility-small"
          title={
            areAllLicensesPending
              ? "More documents will appear here once your license is approved"
              : "There are no workplaces in your area requiring additional documents"
          }
          description="You can still upload miscellaneous documents if needed"
        />
      )}

      {hasApprovedDocuments && (
        <Accordion
          expanded={completedDocumentsExpanded}
          title="Completed Documents"
          count={completedDocuments?.length}
          onChange={(_, newExpanded) => {
            setCompletedDocumentsExpanded(newExpanded);
          }}
        >
          <DocumentsPageWorkplaceSpecificDocumentsSection
            documents={completedDocuments}
            hasNextPage={hasNextPageCompleted ?? false}
            isLoadingMore={isLoadingMoreCompleted}
            onLoadMore={async () => {
              await fetchNextPageCompleted();
            }}
          />
        </Accordion>
      )}

      {hasOptionalDocuments && (
        <Accordion
          expanded={optionalDocumentsExpanded}
          title="Optional Documents"
          count={optionalDocuments?.length}
          onChange={(_, newExpanded) => {
            setOptionalDocumentsExpanded(newExpanded);
          }}
        >
          <DocumentsPageWorkplaceSpecificDocumentsSection
            documents={optionalDocuments}
            hasNextPage={hasNextPageOptional ?? false}
            isLoadingMore={isLoadingMoreOptional}
            onLoadMore={async () => {
              await fetchNextPageOptional();
            }}
          />
        </Accordion>
      )}

      <UploadMiscDocumentButton />
    </Stack>
  );
}
